import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { getPartySaveErrorResource } from "Api/Mutations/Party/getPartySaveErrorResource";
import { usePartyMutation } from "Api/Mutations/Party/usePartyMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import {
  AddressUncommittedProvider,
  useAddressUncommittedTracker,
} from "Components/Shared/Inputs/Form/AddressUncommittedContext";
import { PartyIdentificationImagesViewer } from "Components/Shared/PartyIdentificationImages/PartyIdentificationImagesViewer";
import { FirstDocumentUpload } from "Components/UserAccount/Shared/FirstDocumentUpload";
import { SecondDocumentUpload } from "Components/UserAccount/Shared/SecondDocumentUpload";
import { RelatedPartyDocumentsReupload } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedPartyDocumentsReupload";
import { RelatedPartyFormFields } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedPartyFormFields";
import { StyledForm } from "Components/Users/UserDetailPageContent/Components/StyledForm";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import {
  createRelatedPartyValidationSchema,
  type RelatedPartyFormData,
  type RelatedPartyFormDataInput,
} from "Components/Users/schemas";
import { mapOtherRelatedPartyDtoToFormData } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/utils";
import { useIsCurrentUserAdmin } from "Hooks/useIsCurrentUserAdmin";
import { useCheckPermission } from "Hooks/usePermission";
import {
  type RelationType,
  PartyType,
  Permission,
  VatStatus,
} from "Infrastructure/Api/Api";
import type { PartyDto } from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { isBusinessRelationType } from "Models/BusinessRelationTypes";
import { Resources, useResource } from "Translations/Resources";
import { getDocumentExpirationStatus } from "Utils/DocumentExpiration/documentExpirationUtils";
import { EMPTY_PHONE } from "Utils/PhoneUtils";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.RelatedParties.Form;
const IDENTIFICATION_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party.Form
    .PartyIdentification;

type Props = {
  partyData: PartyDto;
  isOwner: boolean;
  relationType: RelationType;
  onSaveSuccess?: () => void;
};

export const OtherRelatedPartyForm = ({
  partyData,
  isOwner,
  relationType,
  onSaveSuccess,
}: Props) => {
  const { t } = useResource();
  const isAdmin = useIsCurrentUserAdmin();
  const canSeeDocuments = useCheckPermission(
    AccessRightCodes.IDDocuments,
    Permission.Read,
  );
  const { hasUncommittedAddress, uncommittedContextValue } =
    useAddressUncommittedTracker();

  const {
    type,
    licenses,
    lastName,
    hasIdentificationsUploaded,
    publicID,
    addresses,
    partyIdentification,
  } = partyData;

  const isFirstDocumentExpired = getDocumentExpirationStatus(
    partyIdentification?.identificationValidTo ?? null,
  ).isExpired;
  const isSecondDocumentExpired = getDocumentExpirationStatus(
    partyIdentification?.secondIdentificationValidTo ?? null,
  ).isExpired;

  // Mirrors the backend guard: only a natural person acting for the company owes a residence.
  const schema = createRelatedPartyValidationSchema(t, type, {
    isResidenceRequired:
      type === PartyType.NaturalPerson && isBusinessRelationType(relationType),
  });

  const formMethods = useForm<
    RelatedPartyFormDataInput,
    unknown,
    RelatedPartyFormData
  >({
    resolver: zodResolver(schema),
    defaultValues: mapOtherRelatedPartyDtoToFormData(
      partyData,
      relationType,
      isOwner,
    ),
  });
  const {
    control,
    formState: { isDirty },
    reset,
    handleSubmit,
  } = formMethods;

  const { mutate: updateParty, isPending } = usePartyMutation({
    onSuccess: ({ data }) => {
      reset(mapOtherRelatedPartyDtoToFormData(data, relationType, isOwner));
      toast.success(t(COMPONENT_RESOURCES.Controls.SubmitSuccess));
      onSaveSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(
        t(
          getPartySaveErrorResource(
            error,
            COMPONENT_RESOURCES.Controls.SubmitError,
          ),
        ),
      );
    },
  });

  const onSubmit = (data: RelatedPartyFormData) => {
    if (hasUncommittedAddress) {
      toast.error(t(Resources.Components.Shared.AddressInput.UncommittedQuery));
      return;
    }

    if (!data.publicID) {
      logError(new Error("Public ID is missing"), { data }, false);
      return;
    }

    updateParty({
      publicID: data.publicID || "",
      // Licenses kept from the API; verifiedPhone and vatStatus are required by
      // the DTO shape only — the server ignores them on updates.
      data: {
        ...data,
        licenses,
        verifiedPhone: EMPTY_PHONE,
        vatStatus: VatStatus.Unknown,
      },
    });
  };

  const fileNamePrefix = `${t(IDENTIFICATION_RESOURCES.IdentificationsDownloadPrefix)}${lastName || ""}`;

  const firstDocumentImagesSlot =
    hasIdentificationsUploaded && publicID ? (
      <>
        <Box mb={5}>
          <PartyIdentificationImagesViewer
            documentGroup="first"
            fileNamePrefix={fileNamePrefix}
            partyPublicID={publicID}
          />
        </Box>
        <FirstDocumentUpload
          partyPublicID={publicID}
          isExpired={isFirstDocumentExpired}
        />
      </>
    ) : (
      t(IDENTIFICATION_RESOURCES.NoIdentificationsUploaded)
    );

  const secondDocumentImagesSlot =
    hasIdentificationsUploaded && publicID ? (
      <>
        <Box mb={5}>
          <PartyIdentificationImagesViewer
            documentGroup="second"
            fileNamePrefix={fileNamePrefix}
            partyPublicID={publicID}
          />
        </Box>
        <SecondDocumentUpload
          partyPublicID={publicID}
          isExpired={isSecondDocumentExpired}
        />
      </>
    ) : undefined;

  return (
    <FormProvider {...formMethods}>
      <AddressUncommittedProvider value={uncommittedContextValue}>
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <RelatedPartyFormFields
            control={control}
            addresses={addresses}
            partyPublicID={publicID ?? undefined}
            firstDocumentImagesSlot={firstDocumentImagesSlot}
            secondDocumentImagesSlot={secondDocumentImagesSlot}
            areIdentityFieldsReadOnly={!isAdmin}
          />
          <PrimaryButton
            fullWidth
            type="submit"
            disabled={!isDirty}
            isLoading={isPending}
            variant="contained"
            color="primary"
          >
            {t(COMPONENT_RESOURCES.Controls.Save)}
          </PrimaryButton>
        </StyledForm>
      </AddressUncommittedProvider>
      {!canSeeDocuments && hasIdentificationsUploaded && publicID && (
        <Box mt={5}>
          <RelatedPartyDocumentsReupload partyData={partyData} />
        </Box>
      )}
    </FormProvider>
  );
};
